import { useEffect, useState } from "react";
import {
  fetchManagerSystemStatus,
  triggerManagerPricingDashboard,
} from "../../api/client.js";
import type { MiniAppManagerSystemStatus } from "../../api/types.js";
import { messages } from "../../i18n/messages.js";
import { AppButton } from "../ui/AppButton.js";

interface ManagerCabinetViewProps {
  onBack: () => void;
}

export function ManagerCabinetView({ onBack }: ManagerCabinetViewProps) {
  const [status, setStatus] = useState<MiniAppManagerSystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [pricingSending, setPricingSending] = useState(false);
  const [pricingSent, setPricingSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      try {
        const data = await fetchManagerSystemStatus();
        if (!cancelled) {
          setStatus(data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : messages.manager.statusFailed);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleOpenPricing() {
    setPricingSending(true);
    setError(null);
    try {
      await triggerManagerPricingDashboard();
      setPricingSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : messages.manager.pricingFailed);
    } finally {
      setPricingSending(false);
    }
  }

  return (
    <section className="flex flex-1 flex-col gap-4 px-4 py-4 pb-28">
      <button
        type="button"
        onClick={onBack}
        className="min-h-11 text-left text-sm font-medium text-[var(--tg-theme-link-color,#3390ec)]"
      >
        {messages.manager.back}
      </button>

      <h2 className="text-lg font-semibold">{messages.manager.title}</h2>

      <div className="grid gap-3">
        <AppButton disabled={pricingSending} onClick={() => void handleOpenPricing()}>
          {pricingSent ? messages.manager.pricingSent : messages.manager.openPricing}
        </AppButton>
      </div>

      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      <div className="rounded-[16px] border border-black/10 bg-[var(--tg-theme-secondary-bg-color,#f4f4f5)] p-4">
        <h3 className="text-sm font-semibold">{messages.manager.systemStatusTitle}</h3>
        {loading ? <p className="mt-2 text-sm opacity-70">{messages.manager.statusLoading}</p> : null}
        {!loading && status ? (
          <dl className="mt-3 space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.envLabel}</dt>
              <dd className="font-medium">{status.environment}</dd>
            </div>
            {status.worker_version ? (
              <div className="flex justify-between gap-3">
                <dt className="opacity-60">{messages.manager.workerLabel}</dt>
                <dd className="font-mono text-xs">{status.worker_version.slice(0, 12)}…</dd>
              </div>
            ) : null}
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.catalogCountLabel}</dt>
              <dd className="font-medium">{status.catalog_product_count ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.resolvedLabel}</dt>
              <dd className="font-medium">{status.products_resolved ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.unresolvedLabel}</dt>
              <dd className="font-medium">{status.products_unresolved ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.promSyncLabel}</dt>
              <dd className="font-medium">{status.prom_sync_status ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.fxLabel}</dt>
              <dd className="font-medium">{status.fx_usd_uah ?? "—"}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="opacity-60">{messages.manager.roundingLabel}</dt>
              <dd className="text-right font-medium">{status.customer_rounding_mode ?? "—"}</dd>
            </div>
          </dl>
        ) : null}
      </div>
    </section>
  );
}
